import useAuth from '@nobia/zeus-components/lib/auth/useAuth'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useStores } from '../components/store'

const initialSelectedStore = JSON.parse(
  localStorage.getItem('sales-tool-store')
)

const AccountContext = React.createContext({
  store: initialSelectedStore,
  stores: [],
  loading: false,
})

export const useAccount = () => React.useContext(AccountContext)

export const AccountProvider = props => {
  const { profile } = useAuth()
  const [selectedStore, setSelectedStore] = React.useState(initialSelectedStore)
  const { stores: queryStores, loading: loadingStores } = useStores()

  const filterStores = useCallback(
    userStoreIds =>
      (userStoreIds || [])
        .map(id => +id)
        .map(id => ({
          id: `${id}`,
          label: queryStores.find(s => s.id === id)?.storeName,
        }))
        .filter(s => s.label),
    [queryStores]
  )

  const { stores, loading } = useMemo(() => {
    if (loadingStores) {
      return { stores: [], loading: true }
    }
    if (queryStores && profile) {
      const admin = profile?.roles?.includes('sales-tool-admin') || false
      const profileIds =
        typeof profile.store_ids === 'string'
          ? [profile.store_ids]
          : profile.store_ids
      const adminIds = admin ? queryStores.map(s => s.id) : []
      const ids = admin ? adminIds : profileIds
      return { stores: filterStores(ids), loading: false }
    }
    return { stores: [], loading: false }
  }, [loadingStores, queryStores, profile, filterStores])

  useEffect(() => {
    if (!stores || stores.length === 0) {
      return
    }
    const selected = stores.find(s => s.id === selectedStore?.id)
    if (!selectedStore || !selected) {
      setSelectedStore({ id: stores[0].id, label: stores[0].label })
    }
  }, [selectedStore, stores])

  const selectedStoreRef = useRef(selectedStore)
  const storesRef = useRef(stores)

  useEffect(() => {
    selectedStoreRef.current = selectedStore
  }, [selectedStore])

  useEffect(() => {
    storesRef.current = stores
  }, [stores])

  const setStore = useCallback(storeId => {
    if (!storeId) {
      return
    }
    if (selectedStoreRef.current?.id === `${storeId}`) {
      return
    }
    const inStoresList =
      storesRef.current.map(s => +s.id).indexOf(+storeId) >= 0
    if (!inStoresList) {
      return
    }
    const storeName = storesRef.current.find(s => s.id === `${storeId}`)?.label
    setSelectedStore({ id: `${storeId}`, label: storeName })
  }, [])

  useEffect(() => {
    localStorage.setItem('sales-tool-store', JSON.stringify(selectedStore))
  }, [selectedStore])

  const value = useMemo(
    () => ({
      store: selectedStore,
      stores,
      loading,
      setStore,
    }),
    [selectedStore, stores, loading, setStore]
  )

  return (
    <AccountContext.Provider value={value}>
      {props.children}
    </AccountContext.Provider>
  )
}

AccountProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
